import React, { useEffect, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import styled from 'styled-components';

import { usePrevious } from 'common/hooks';

import SectionsRenderer, { SectionsRendererData } from './SectionsRenderer';
import { FullSelection } from '../../../pages/SharedMenuEditorPage/types';
import { TranslationLabelItem, TranslationSectionItem } from '../types';
import { isProductInfoField } from '../utils';

type TaskIdentifier = {
  key: string;
  pos: 'right' | 'left';
};

type Props = {
  className?: string;
  fullSelection: FullSelection;
  leftLanguage: Language | null;
  rightLanguage: Language | null;
  sections: (TranslationLabelItem | TranslationSectionItem)[];
  primaryLanguage: Language;
  erroredTasks: TaskIdentifier[];
  pendingTasks: TaskIdentifier[];
  onSaveTranslation: (data: { value: string; item: TranslationSectionItem; pos: 'right' | 'left' }) => void;
};

const Root = styled.div``;

const Sections = ({
  className,
  fullSelection,
  leftLanguage,
  rightLanguage,
  sections,
  primaryLanguage,
  erroredTasks,
  pendingTasks,
  onSaveTranslation,
}: Props) => {
  const listRef = useRef<VariableSizeList>(null);

  const previousSectionsLength = usePrevious(sections.length);

  const virtualListHeights = useMemo(
    () =>
      sections.map(el => {
        if (el.itemType === 'section') {
          return 94;
        }
        if (el.itemType === 'subsection') {
          return 40;
        }
        if (el.field === 'description' || isProductInfoField(el.field)) {
          return 149;
        }
        return 68;
      }),
    [sections],
  );

  useEffect(() => {
    if (sections.length !== previousSectionsLength) {
      listRef.current?.resetAfterIndex(0);
    }
  }, [previousSectionsLength, sections.length]);

  if (!leftLanguage && !rightLanguage) {
    return <div className={className} />;
  }

  return (
    <Root className={className}>
      <AutoSizer disableWidth>
        {({ height }) => (
          <VariableSizeList<SectionsRendererData>
            ref={listRef}
            height={height}
            width="100%"
            itemData={{
              list: sections,
              fullSelection,
              leftLanguage,
              rightLanguage,
              primaryLanguage,
              erroredTasks,
              pendingTasks,
              onSaveTranslation,
            }}
            itemCount={sections.length}
            itemSize={index => virtualListHeights[index]}
            itemKey={(index, data) => {
              const row = data.list[index];
              return row.itemType === 'section' || row.itemType === 'subsection'
                ? `${row.label}${index}`
                : `${row.itemId}${index}`;
            }}
            overscanCount={20}
          >
            {SectionsRenderer}
          </VariableSizeList>
        )}
      </AutoSizer>
    </Root>
  );
};

export default Sections;
